import { useState } from 'react';
import { Bedrag, Knop } from '@qc/ui';
import type { Evenement } from '@qc/db';
import { Veld } from '../auth/Veld.js';
import { useAfrekening, type VastgesteldeFactuur } from './useAfrekening.js';

/** Geen sluitend bewijs, alleen een sanity-check vóór het opslaan. */
const LIJKT_EEN_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface AfrekeningProps {
  evenement: Evenement;
}

/**
 * Het controlescherm vóór verzending.
 *
 * Totaal binnengekomen tegenover totaal uit te betalen. Het verschil moet nul
 * zijn; anders gaat er niets naar de bank. Dat is geen formaliteit maar de
 * laatste zekerheid dat er geen omzet zoek is.
 */
type Verzendstatus = 'bezig' | 'verzonden' | 'mislukt';

export function Afrekening({ evenement }: AfrekeningProps) {
  const beeld = useAfrekening(evenement);
  const [verzendstatus, setVerzendstatus] = useState<Record<string, Verzendstatus>>({});
  const [verzendfout, setVerzendfout] = useState<Record<string, string>>({});
  const [emailInvoer, setEmailInvoer] = useState<Record<string, string>>({});
  const [emailBezig, setEmailBezig] = useState<Record<string, boolean>>({});
  const [emailFout, setEmailFout] = useState<Record<string, string>>({});
  const [pdfFout, setPdfFout] = useState<Record<string, string>>({});
  const [pdfBezig, setPdfBezig] = useState<Record<string, boolean>>({});
  const [alleBezig, setAlleBezig] = useState(false);
  const [alleMelding, setAlleMelding] = useState<string | null>(null);

  async function versturenAlle(): Promise<void> {
    setAlleBezig(true);
    setAlleMelding(null);
    const resultaat = await beeld.verstuurAlleFacturen();
    setAlleBezig(false);
    if (resultaat.aantalVerstuurd === 0 && resultaat.fouten.length === 0) {
      setAlleMelding('Er staat niets meer klaar om te versturen.');
      return;
    }
    const delen = [];
    if (resultaat.aantalVerstuurd > 0) {
      delen.push(
        `${resultaat.aantalVerstuurd} ${resultaat.aantalVerstuurd === 1 ? 'factuur' : 'facturen'} verstuurd`,
      );
    }
    if (resultaat.fouten.length > 0) {
      delen.push(
        `${resultaat.fouten.length} mislukt (${resultaat.fouten.map((f) => f.factuurnummer).join(', ')})`,
      );
    }
    setAlleMelding(delen.join(', ') + '.');
  }

  async function versturen(vast: VastgesteldeFactuur): Promise<void> {
    const nummer = vast.factuur.factuurnummer;
    setVerzendstatus((huidig) => ({ ...huidig, [nummer]: 'bezig' }));
    const resultaat = await beeld.verstuurFactuur(vast);
    setVerzendstatus((huidig) => ({ ...huidig, [nummer]: resultaat.ok ? 'verzonden' : 'mislukt' }));
    if (!resultaat.ok) {
      setVerzendfout((huidig) => ({ ...huidig, [nummer]: resultaat.melding ?? 'Onbekende fout.' }));
    }
  }

  async function slaEmailOp(vast: VastgesteldeFactuur): Promise<void> {
    const nummer = vast.factuur.factuurnummer;
    const email = (emailInvoer[nummer] ?? '').trim();
    if (!LIJKT_EEN_EMAIL.test(email)) {
      setEmailFout((huidig) => ({ ...huidig, [nummer]: 'Dit lijkt geen geldig e-mailadres.' }));
      return;
    }
    setEmailBezig((huidig) => ({ ...huidig, [nummer]: true }));
    const resultaat = await beeld.stelEmailIn(vast, email);
    setEmailBezig((huidig) => ({ ...huidig, [nummer]: false }));
    if (!resultaat.ok) {
      setEmailFout((huidig) => ({
        ...huidig,
        [nummer]: resultaat.melding ?? 'Opslaan is mislukt.',
      }));
      return;
    }
    setEmailFout((huidig) => {
      const kopie = { ...huidig };
      delete kopie[nummer];
      return kopie;
    });
  }

  async function bekijkPdf(vast: VastgesteldeFactuur): Promise<void> {
    const nummer = vast.factuur.factuurnummer;
    // Meteen een leeg tabblad openen, vóór de await: browsers blokkeren een
    // window.open() die pas na een netwerkaanroep komt, omdat die dan niet
    // meer als rechtstreeks gevolg van de klik geldt.
    const tabblad = window.open('', '_blank');
    setPdfFout((huidig) => {
      const kopie = { ...huidig };
      delete kopie[nummer];
      return kopie;
    });
    setPdfBezig((huidig) => ({ ...huidig, [nummer]: true }));
    const resultaat = await beeld.haalPdfUrl(vast);
    setPdfBezig((huidig) => ({ ...huidig, [nummer]: false }));
    if (!resultaat.ok || !resultaat.url) {
      tabblad?.close();
      setPdfFout((huidig) => ({
        ...huidig,
        [nummer]: resultaat.melding ?? 'De PDF kon niet worden opgehaald.',
      }));
      return;
    }
    if (tabblad) tabblad.location.href = resultaat.url;
    else window.open(resultaat.url, '_blank');
  }

  if (beeld.laden) return <p className="text-tekst-zacht">Bezig met rekenen…</p>;
  if (beeld.afrekening === null) {
    return (
      <p className="text-tekst-zacht">
        Er valt nog niets af te rekenen. Zodra er kramen en bonnen zijn, staat het hier.
      </p>
    );
  }

  const { afrekening, sepa } = beeld;
  const sluit = afrekening.controle.sluit;

  async function downloadSepa(): Promise<void> {
    if (sepa === null) return;
    const blob = new Blob([sepa.xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = sepa.bestandsnaam;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    // Markeert pas ná de download, niet ervoor: het bestand moet er hoe dan
    // ook liggen, ook als het markeren om wat voor reden dan ook mislukt.
    await beeld.markeerUitbetalingBatch();
  }

  return (
    <div className="grid gap-8">
      <section
        className={`rounded-tegel border p-6 ${sluit ? 'border-goed bg-vlak' : 'border-fout bg-vlak'}`}
      >
        <h2 className="mb-4 text-xl font-bold">Controle</h2>
        <dl className="grid gap-2">
          <div className="flex justify-between text-lg">
            <dt>Totaal binnengekomen</dt>
            <dd>
              <Bedrag centen={afrekening.ontvangenCent} />
            </dd>
          </div>
          <div className="flex justify-between text-lg">
            <dt>Totaal uit te betalen en in te houden</dt>
            <dd>
              <Bedrag centen={afrekening.controle.totaalUitCent} />
            </dd>
          </div>
          <div
            className={`flex justify-between border-t border-rand pt-2 text-2xl font-bold ${
              sluit ? 'text-goed' : 'text-fout'
            }`}
          >
            <dt>Verschil</dt>
            <dd>
              <Bedrag centen={afrekening.controle.verschilCent} />
            </dd>
          </div>
        </dl>
        <p className="mt-3 text-tekst-zacht">
          {sluit
            ? 'Het verschil is nul. Het bestand mag naar de bank.'
            : 'Zolang het verschil niet nul is gaat er niets naar de bank.'}
        </p>
      </section>

      <section>
        <h2 className="mb-2 text-xl font-bold">Per kraam</h2>
        <div className="overflow-x-auto rounded-tegel border border-rand bg-vlak">
          <table className="w-full min-w-[46rem] text-left">
            <thead className="border-b border-rand text-tekst-zacht">
              <tr>
                <th className="p-4 font-normal">Kraam</th>
                <th className="p-4 text-right font-normal">Omzet</th>
                <th className="p-4 text-right font-normal">Statiegeld</th>
                <th className="p-4 text-right font-normal">Commissie</th>
                <th className="p-4 text-right font-normal">Kosten</th>
                <th className="p-4 text-right font-normal">Uit te betalen</th>
              </tr>
            </thead>
            <tbody>
              {afrekening.kramen.map((kraam) => (
                <tr key={kraam.kraamdeelnameId} className="border-b border-rand last:border-0">
                  <td className="p-4">
                    {kraam.kraamnaam}
                    {kraam.ontvanger === 'organisator' && (
                      <span className="block text-base text-tekst-zacht">
                        gaat naar de organisator
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <Bedrag centen={kraam.brutoOmzetCent} />
                  </td>
                  <td className="p-4 text-right text-tekst-zacht">
                    <Bedrag centen={kraam.statiegeldSaldoCent} />
                  </td>
                  <td className="p-4 text-right text-tekst-zacht">
                    <Bedrag centen={-kraam.commissie.commissieCent} />
                  </td>
                  <td className="p-4 text-right text-tekst-zacht">
                    <Bedrag centen={-(kraam.transactiekostenCent + kraam.inhoudingenCent)} />
                  </td>
                  <td className="p-4 text-right font-semibold">
                    <Bedrag centen={kraam.uitbetaling.nuUitTeBetalenCent} />
                  </td>
                </tr>
              ))}
              <tr className="border-t border-rand">
                <td className="p-4">{afrekening.organisator.regels[0]?.omschrijving}</td>
                <td colSpan={4} />
                <td className="p-4 text-right font-semibold">
                  <Bedrag centen={afrekening.organisator.uitTeBetalenCent} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {afrekening.organisator.organisatorMoetBijbetalen && (
          <p className="mt-3 text-waarschuwing">
            De organisator moet per saldo bijbetalen. Hij komt niet in het SEPA-bestand; hij krijgt
            een factuur.
          </p>
        )}
      </section>

      {beeld.marge !== null && (
        <section className="rounded-tegel border border-rand bg-vlak p-6">
          <h2 className="mb-4 text-xl font-bold">Wat jij overhoudt</h2>
          <dl className="grid gap-2">
            <div className="flex justify-between">
              <dt className="text-tekst-zacht">Kraamsets (exclusief BTW)</dt>
              <dd>
                <Bedrag centen={afrekening.kraamsetkosten.nettoCent} />
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-tekst-zacht">Doorbelaste transactiekosten</dt>
              <dd>
                <Bedrag centen={afrekening.transactiekostenTotaalCent} />
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-tekst-zacht">Betaald aan de betaalpartij</dt>
              <dd>
                <Bedrag centen={-beeld.inkoopTransactiekostenCent} />
              </dd>
            </div>
            {afrekening.inhoudingenTotaalCent !== 0 && (
              <div className="flex justify-between">
                <dt className="text-tekst-zacht">Inhoudingen apparatuur</dt>
                <dd>
                  <Bedrag centen={afrekening.inhoudingenTotaalCent} />
                </dd>
              </div>
            )}
            {beeld.marge.kostenCent - beeld.inkoopTransactiekostenCent !== 0 && (
              <div className="flex justify-between">
                <dt className="text-tekst-zacht">Eigen kosten</dt>
                <dd>
                  <Bedrag centen={-(beeld.marge.kostenCent - beeld.inkoopTransactiekostenCent)} />
                </dd>
              </div>
            )}
            <div className="flex justify-between border-t border-rand pt-2 text-2xl font-bold">
              <dt>Marge</dt>
              <dd>
                <Bedrag centen={beeld.marge.margeCent} />
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-tekst-zacht">
            Op de transactiekosten hou je <Bedrag centen={beeld.marge.transactiemargeCent} /> over.
            Staat dat op nul, dan belast je precies door wat je zelf betaalt — en dat is ook goed.
          </p>
        </section>
      )}

      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold">Facturen</h2>
          <Knop nadruk="stil" disabled={alleBezig} onClick={() => void versturenAlle()}>
            {alleBezig ? 'Bezig…' : 'Verstuur alle facturen'}
          </Knop>
        </div>
        {alleMelding !== null && <p className="mb-2 text-tekst-zacht">{alleMelding}</p>}
        <ul className="grid gap-2">
          {beeld.facturen.map((vast) => {
            const factuur = vast.factuur;
            const ontvanger = beeld.ontvangerPerFactuur[factuur.factuurnummer];
            const status = verzendstatus[factuur.factuurnummer];
            // vast.status komt uit de database en overleeft dus een herlaad;
            // status is alleen de status van déze sessie, voor het "bezig"-
            // moment vlak na de klik dat de rij nog niet heeft bijgewerkt.
            const verzonden = vast.status === 'verstuurd' || status === 'verzonden';
            return (
              <li
                key={factuur.factuurnummer}
                className="flex flex-wrap items-center justify-between gap-4 rounded-tegel border border-rand bg-vlak p-4"
              >
                <span className="min-w-0">
                  <span className="block truncate text-lg">
                    {factuur.van.naam} → {factuur.aan.naam}
                  </span>
                  <span className="block text-base text-tekst-zacht">
                    {factuur.factuurnummer} · netto <Bedrag centen={factuur.nettoCent} /> · BTW{' '}
                    <Bedrag centen={factuur.btwCent} />
                  </span>
                  {status === 'mislukt' && (
                    <span className="block text-base text-fout">
                      {verzendfout[factuur.factuurnummer]}
                    </span>
                  )}
                  {pdfFout[factuur.factuurnummer] !== undefined && (
                    <span className="block text-base text-fout">
                      {pdfFout[factuur.factuurnummer]}
                    </span>
                  )}
                </span>
                {ontvanger === undefined && !verzonden ? (
                  <div className="flex w-full flex-wrap items-end gap-3 sm:w-auto">
                    <div className="min-w-[16rem] grow sm:grow-0">
                      <Veld
                        label="E-mailadres ontbreekt — vul aan"
                        type="email"
                        value={emailInvoer[factuur.factuurnummer] ?? ''}
                        onChange={(e) =>
                          setEmailInvoer((huidig) => ({
                            ...huidig,
                            [factuur.factuurnummer]: e.target.value,
                          }))
                        }
                        fout={emailFout[factuur.factuurnummer] !== undefined}
                        toelichting={emailFout[factuur.factuurnummer] ?? null}
                      />
                    </div>
                    <Knop
                      nadruk="gewoon"
                      disabled={emailBezig[factuur.factuurnummer] === true}
                      onClick={() => void slaEmailOp(vast)}
                    >
                      {emailBezig[factuur.factuurnummer] === true ? 'Bezig…' : 'Opslaan'}
                    </Knop>
                    <Knop
                      nadruk="stil"
                      disabled={pdfBezig[factuur.factuurnummer] === true}
                      onClick={() => void bekijkPdf(vast)}
                    >
                      {pdfBezig[factuur.factuurnummer] === true ? 'Bezig…' : 'PDF'}
                    </Knop>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <Bedrag centen={factuur.brutoCent} className="text-lg font-semibold" />
                    <Knop
                      nadruk="stil"
                      disabled={pdfBezig[factuur.factuurnummer] === true}
                      onClick={() => void bekijkPdf(vast)}
                    >
                      {pdfBezig[factuur.factuurnummer] === true ? 'Bezig…' : 'PDF'}
                    </Knop>
                    {verzonden ? (
                      <span className="text-base text-goed">
                        Verzonden{ontvanger ? ` naar ${ontvanger[0]}` : ''}
                      </span>
                    ) : (
                      <Knop
                        nadruk="gewoon"
                        disabled={status === 'bezig'}
                        onClick={() => void versturen(vast)}
                      >
                        {status === 'bezig' ? 'Bezig…' : 'Verstuur'}
                      </Knop>
                    )}
                  </div>
                )}
              </li>
            );
          })}
          {beeld.facturen.length === 0 && beeld.factuurfout === null && (
            <li className="text-tekst-zacht">Er valt nog niets te factureren.</li>
          )}
        </ul>
        {beeld.factuurfout !== null && (
          <p className="mt-3 text-fout">
            De facturen konden niet worden opgemaakt: {beeld.factuurfout}
          </p>
        )}
      </section>

      <section className="rounded-tegel border border-rand bg-vlak p-6">
        <h2 className="mb-2 text-xl font-bold">Naar de bank</h2>
        {sepa === null ? (
          <p className="text-fout">{beeld.fout ?? 'Het SEPA-bestand kon niet worden gemaakt.'}</p>
        ) : (
          <>
            <p className="mb-3 text-tekst-zacht">
              {sepa.aantalOpdrachten}{' '}
              {sepa.aantalOpdrachten === 1 ? 'overboeking' : 'overboekingen'}, samen{' '}
              <Bedrag centen={sepa.totaalCent} />.
            </p>
            {sepa.overgeslagen.length > 0 && (
              <ul className="mb-3 grid gap-2">
                {sepa.overgeslagen.map((overgeslagen) => (
                  <li
                    key={overgeslagen.naam}
                    className="rounded-tegel bg-vlak-hoog p-3 text-waarschuwing"
                  >
                    {overgeslagen.naam} zit er niet in: {overgeslagen.reden}
                  </li>
                ))}
              </ul>
            )}
            <Knop
              nadruk={sluit ? 'hoofd' : 'gewoon'}
              disabled={!sluit || sepa.aantalOpdrachten === 0}
              onClick={() => void downloadSepa()}
              className="w-full"
            >
              SEPA-bestand downloaden
            </Knop>
            {!sluit && (
              <p className="mt-3 text-waarschuwing">
                Het verschil is niet nul, dus je kunt het bestand nog niet downloaden.
              </p>
            )}
            {beeld.batchVerstuurdOp !== null && (
              <p className="mt-3 text-tekst-zacht">
                Dit bestand is al eerder gedownload, op{' '}
                {new Date(beeld.batchVerstuurdOp).toLocaleString('nl-NL')}. Downloaden geeft
                dezelfde uitbetalingen terug, geen nieuwe.
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
